import http, { httpMultipart } from "./http";
import type {
  GenerateMonthlyReportForm,
  ReportClientCard,
  ReportRecord,
  ReportMonthSlot,
  ReportsClientsResponse,
} from "@/types/reports";

const resolveReportUrl = (url?: string | null): string => {
  const rawUrl = String(url || "").trim();

  if (!rawUrl) {
    return "";
  }

  try {
    const parsedUrl = new URL(
      rawUrl,
      typeof window !== "undefined" ? window.location.origin : "http://localhost"
    );

    const monthlyPdfMatch = parsedUrl.pathname.match(
      /^\/reports\/(\d+)\/download-pdf\/?$/
    );
    if (monthlyPdfMatch) {
      return `/api/reports/${monthlyPdfMatch[1]}/download/pdf${parsedUrl.search}${parsedUrl.hash}`;
    }

    const monthlyHtmlMatch = parsedUrl.pathname.match(
      /^\/reports\/(\d+)\/download-html\/?$/
    );
    if (monthlyHtmlMatch) {
      return `/api/reports/${monthlyHtmlMatch[1]}/download/html${parsedUrl.search}${parsedUrl.hash}`;
    }

    if (typeof window !== "undefined") {
      const samePathTypes = ["/reports/", "/storage/"];
      const shouldUseCurrentOrigin = samePathTypes.some((prefix) =>
        parsedUrl.pathname.startsWith(prefix)
      );

      if (shouldUseCurrentOrigin && parsedUrl.origin !== window.location.origin) {
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      }
    }

    return parsedUrl.toString();
  } catch {
    return rawUrl;
  }
};

const resolveReportPreviewHtmlUrl = (report: ReportRecord): string => {
  const previewUrl = resolveReportUrl(report.preview_html_url || report.preview_url);

  if (!previewUrl) {
    return "";
  }

  try {
    const parsedUrl = new URL(
      previewUrl,
      typeof window !== "undefined" ? window.location.origin : "http://localhost"
    );

    if (
      parsedUrl.pathname.startsWith("/reports/") &&
      parsedUrl.pathname.endsWith("/preview")
    ) {
      return `/api${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    return previewUrl;
  } catch {
    if (previewUrl.startsWith("/reports/") && previewUrl.endsWith("/preview")) {
      return `/api${previewUrl}`;
    }

    return previewUrl;
  }
};

const buildPreviewDocument = (html: string): string => {
  const trimmedHtml = String(html || "").trim();
  if (!trimmedHtml) {
    return "";
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const baseTag = origin ? `<base href="${origin}/">` : "";
  const hasHtmlRoot = /<html[\s>]/i.test(trimmedHtml);

  if (hasHtmlRoot) {
    if (/<base\s/i.test(trimmedHtml)) {
      return trimmedHtml;
    }

    if (/<head[\s>]/i.test(trimmedHtml)) {
      return trimmedHtml.replace(/<head([^>]*)>/i, `<head$1>${baseTag}`);
    }

    return trimmedHtml.replace(
      /<html([^>]*)>/i,
      `<html$1><head>${baseTag}<meta charset="utf-8"></head>`
    );
  }

  return `<!doctype html><html><head>${baseTag}<meta charset="utf-8"></head><body>${trimmedHtml}</body></html>`;
};

export const openHtmlDocument = (html: string, targetWindow?: Window | null) => {
  const documentHtml = buildPreviewDocument(html);
  if (!documentHtml || typeof window === "undefined") {
    return;
  }

  if (targetWindow && !targetWindow.closed) {
    try {
      targetWindow.document.open();
      targetWindow.document.write(documentHtml);
      targetWindow.document.close();
      return;
    } catch (writeErr) {
      // Fall through to blob URL if direct document write is disallowed
      console.warn("Direct document write fallback triggered:", writeErr);
    }
  }

  const blob = new Blob([documentHtml], { type: "text/html;charset=utf-8" });
  const blobUrl = window.URL.createObjectURL(blob);

  if (targetWindow && !targetWindow.closed) {
    targetWindow.location.href = blobUrl;
  } else {
    const popup = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (!popup) {
      window.URL.revokeObjectURL(blobUrl);
      console.warn("Popup blocked by browser.");
      return;
    }
  }

  setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 60000);
};

const extractDownloadFilename = (
  contentDisposition?: string | null,
  fallback = "report.pdf"
): string => {
  const header = String(contentDisposition || "").trim();
  if (!header) {
    return fallback;
  }

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const basicMatch = header.match(/filename="?([^";]+)"?/i);
  if (basicMatch?.[1]) {
    return basicMatch[1];
  }

  return fallback;
};

const toFormData = (
  payload: GenerateMonthlyReportForm,
  files: Record<string, File[]>,
  extractedPayload?: Record<string, string>
) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => formData.append(`${key}[]`, String(item)));
      return;
    }

    if (typeof value === "boolean") {
      formData.append(key, value ? "1" : "0");
      return;
    }

    if (value === undefined || value === null) {
      return;
    }

    formData.append(key, String(value));
  });

  Object.entries(files).forEach(([field, list]) => {
    list.forEach((file) => formData.append(`${field}[]`, file));
  });

  if (extractedPayload) {
    Object.entries(extractedPayload).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  return formData;
};

export const getReportClients = async (): Promise<ReportClientCard[]> => {
  const response = await http.get<ReportsClientsResponse>("/api/reports/clients");
  return response.data.clients || [];
};

export const getReportClientMonths = async (
  clientId: string
): Promise<ReportMonthSlot[]> => {
  const response = await http.get<{ months: ReportMonthSlot[] }>(
    `/api/reports/client/${clientId}/months`
  );
  return response.data.months || [];
};

export const createMonthlyReport = async (
  payload: GenerateMonthlyReportForm,
  files: Record<string, File[]>,
  extractedPayload?: Record<string, string>
): Promise<ReportRecord> => {
  const formData = toFormData(payload, files, extractedPayload);
  const { data } = await httpMultipart.post<{ report: ReportRecord }>(
    "/api/reports/generate",
    formData
  );
  return data.report;
};

export const updateMonthlyReport = async (
  reportId: number,
  payload: GenerateMonthlyReportForm,
  files: Record<string, File[]>,
  extractedPayload?: Record<string, string>
): Promise<ReportRecord> => {
  const formData = toFormData(payload, files, extractedPayload);
  formData.append("_method", "PUT");
  const { data } = await httpMultipart.post<{ report: ReportRecord }>(
    `/api/reports/${reportId}`,
    formData
  );
  return data.report;
};

export const regenerateReportSection = async (
  reportId: number | string,
  sectionKey: string,
  files: File[],
  options?: {
    extractedContents?: string;
    aiModel?: string;
  }
): Promise<ReportRecord> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files[]", file);
  });
  if (options?.extractedContents) {
    formData.append("extracted_contents", options.extractedContents);
  }
  if (options?.aiModel) {
    formData.append("ai_model", options.aiModel);
  }

  const { data } = await httpMultipart.post<{ report: ReportRecord; message?: string }>(
    `/api/reports/${reportId}/sections/${sectionKey}/regenerate`,
    formData
  );
  return data.report;
};

export const publishMonthlyReport = async (reportId: number): Promise<ReportRecord> => {
  const { data } = await http.post<{ report: ReportRecord }>(
    `/api/reports/${reportId}/publish`
  );
  return data.report;
};

export const fetchReportPreviewHtml = async (report: ReportRecord): Promise<string> => {
  const previewUrl = resolveReportPreviewHtmlUrl(report);
  if (!previewUrl) {
    return "";
  }

  const response = await http.get<string>(previewUrl, {
    responseType: "text",
  });

  return response.data || "";
};

export const openReportPreview = (report: ReportRecord) => {
  return fetchReportPreviewHtml(report).then((html) => {
    openHtmlDocument(html);
  });
};

export const openReportPdf = async (report: ReportRecord) => {
  const downloadUrl = resolveReportUrl(report.download_pdf_url);
  if (downloadUrl) {
    const response = await http.get(downloadUrl, {
      responseType: "blob",
    });

    const blobUrl = window.URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = extractDownloadFilename(
      response.headers?.["content-disposition"],
      `monthly-report-${report.client_id}-month-${report.month_number}.pdf`
    );
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(blobUrl);
  }
};

export const openReportHtml = async (
  report: ReportRecord,
  preloadedHtml?: string
) => {
  if (preloadedHtml && preloadedHtml.trim()) {
    openHtmlDocument(preloadedHtml);
    return;
  }

  const targetWindow = typeof window !== "undefined" ? window.open("about:blank", "_blank") : null;
  if (targetWindow) {
    try {
      targetWindow.document.write(
        "<!doctype html><html><head><title>Loading Report...</title><style>body{font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8fbff;color:#0f3557;}</style></head><body><div style='text-align:center'><h3>Loading Report...</h3><p style='color:#64748b;font-size:13px;'>Compiling live document</p></div></body></html>"
      );
    } catch (writeErr) {
      console.warn("Unable to write loading placeholder into target window:", writeErr);
    }
  }

  try {
    const html = await fetchReportPreviewHtml(report);
    openHtmlDocument(html, targetWindow);
  } catch (err) {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.close();
    }
    throw err;
  }
};

export const printReportHtml = async (report: ReportRecord) => {
  const html = await fetchReportPreviewHtml(report);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("sandbox", "allow-same-origin allow-modals");
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    openHtmlDocument(html);
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => iframe.remove(), 1500);
      }
    }, 400);
  };
};

export const uploadReportPdf = async (
  payload: FormData,
  onProgress?: (percentage: number) => void
): Promise<ReportRecord> => {
  const { data } = await httpMultipart.post<{ report: ReportRecord }>(
    "/api/reports/upload-pdf",
    payload,
    { onUploadProgress: (event) => event.total && onProgress?.(Math.round((event.loaded * 100) / event.total)) }
  );
  return data.report;
};

export const replaceReportPdf = async (
  reportId: number,
  payload: FormData,
  onProgress?: (percentage: number) => void
): Promise<ReportRecord> => {
  const { data } = await httpMultipart.post<{ report: ReportRecord }>(
    `/api/reports/${reportId}/replace-pdf`,
    payload,
    { onUploadProgress: (event) => event.total && onProgress?.(Math.round((event.loaded * 100) / event.total)) }
  );
  return data.report;
};

export const openUploadedReportPdf = async (report: ReportRecord) => {
  if (!report.pdf_view_url) throw new Error("The uploaded PDF is not available.");

  // Open synchronously to satisfy popup blockers, then load the protected
  // document through Axios so the authenticated Sanctum request is reused.
  const opened = window.open("about:blank", "_blank");
  if (!opened) {
    throw new Error("The browser blocked the PDF window. Please allow pop-ups and try again.");
  }
  opened.opener = null;

  try {
    const response = await http.get<Blob>(report.pdf_view_url, {
      responseType: "blob",
    });
    const blobUrl = window.URL.createObjectURL(response.data);
    opened.location.href = blobUrl;
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (error) {
    opened.close();
    throw error;
  }
};

export const deleteMonthlyReport = async (reportId: number): Promise<void> => {
  await http.delete(`/api/reports/${reportId}`);
};
